// Tool call group component — renders a group of tool calls in the chat.
// Copied from frontend/src/components/agent/ToolCallGroup.tsx.
// Generic — no Platinum-specific dependencies.
// See ../../docs/90-provenance-map.md.

import React, { memo, useState } from 'react'
import { Box, Typography, Collapse, Button, Tooltip } from '@mui/material'
import type { ToolCallInfo } from '../types.js'
import { getToolDisplayName, getToolCategory, getToolIcon, getToolColour, getToolSummary, parseScriptExecution, stripMcpPrefix } from '../tool-formatters.js'
import CodeCreatedBlock from './CodeCreatedBlock.js'
import ScriptExecutionBlock from './ScriptExecutionBlock.js'
import { CARD_SX, INNER_RADIUS } from './surface.js'
import { collapseBase64 } from '../base64.js'
import SubagentCard from './SubagentCard.js'
import WorkflowCard from './WorkflowCard.js'

const OUTPUT_PREVIEW_LIMIT = 2000
const MAX_IMAGE_BASE64_LENGTH = 7_000_000 // ~5MB binary → ~7MB base64

/** Try to extract base64 image data from tool output JSON. */
export function tryParseImageOutput(output: string): { base64: string; mimeType: string } | null {
  if (output.length > MAX_IMAGE_BASE64_LENGTH) return null
  try {
    const parsed = JSON.parse(output)
    // Direct format: {"type":"image","file":{"base64":"..."}}
    if (parsed?.type === 'image' && parsed?.file?.base64) {
      return { base64: parsed.file.base64, mimeType: parsed.file.mimeType || 'image/png' }
    }
    // Direct Anthropic format: {"type":"image","source":{"data":"...","media_type":"image/png"}}
    if (parsed?.type === 'image' && parsed?.source?.data) {
      return { base64: parsed.source.data, mimeType: parsed.source.media_type || 'image/png' }
    }
    // Array format: [{"type":"image",...}]
    if (Array.isArray(parsed)) {
      const img = parsed.find((item: Record<string, unknown>) => item?.type === 'image' && ((item?.file as Record<string, unknown>)?.base64 || item?.data || (item?.source as Record<string, unknown>)?.data))
      if (img) {
        if ((img.source as Record<string, unknown>)?.data) return { base64: (img.source as Record<string, string>).data, mimeType: (img.source as Record<string, string>).media_type || 'image/png' }
        if (img.data) return { base64: img.data as string, mimeType: (img.mimeType as string) || 'image/png' }
        return { base64: (img.file as Record<string, string>).base64, mimeType: (img.file as Record<string, string>).mimeType || 'image/png' }
      }
    }
    // Content array format: {"content":[{"type":"image",...}]}
    if (Array.isArray(parsed?.content)) {
      const img = parsed.content.find((item: Record<string, unknown>) => item?.type === 'image' && ((item?.file as Record<string, unknown>)?.base64 || item?.data || (item?.source as Record<string, unknown>)?.data))
      if (img) {
        if ((img.source as Record<string, unknown>)?.data) return { base64: (img.source as Record<string, string>).data, mimeType: (img.source as Record<string, string>).media_type || 'image/png' }
        if (img.data) return { base64: img.data as string, mimeType: (img.mimeType as string) || 'image/png' }
        return { base64: (img.file as Record<string, string>).base64, mimeType: (img.file as Record<string, string>).mimeType || 'image/png' }
      }
    }
    return null
  } catch {
    return null
  }
}

const IMAGE_EXTENSIONS = /\.(png|jpg|jpeg|gif|webp|bmp|ico|tiff?)$/i

export function isScreenshotToolCall(tc: ToolCallInfo): boolean {
  const bareName = stripMcpPrefix(tc.name)
  return bareName === 'screenshot_url'
}

export function isImageReadToolCall(tc: ToolCallInfo): boolean {
  const bareName = stripMcpPrefix(tc.name)
  if (bareName !== 'Read') return false
  const filePath = tc.input?.file_path as string | undefined
  return !!filePath && IMAGE_EXTENSIONS.test(filePath)
}

/** Returns true for any tool known to produce image output (Read on image paths, screenshot_url, view_image). */
export function isImageToolCall(tc: ToolCallInfo): boolean {
  const bareName = stripMcpPrefix(tc.name)
  if (bareName === 'screenshot_url' || bareName === 'view_image') return true
  if (bareName === 'Read') {
    const filePath = tc.input?.file_path as string | undefined
    return !!filePath && IMAGE_EXTENSIONS.test(filePath)
  }
  return false
}

interface ToolCallGroupProps {
  toolCalls: ToolCallInfo[]
  /**
   * Drawn inside another card (a sub-agent's tools inside its SubagentCard):
   * the chain takes the inner corner, per the nesting rule in surface.ts.
   */
  nested?: boolean
}

/** The three visual states a tool-chain dot can take. */
export type ToolDotState = 'running' | 'done' | 'failed' | 'cancelled'

export function toolDotState(status: ToolCallInfo['status']): ToolDotState {
  if (status === 'running') return 'running'
  if (status === 'error') return 'failed'
  if (status === 'cancelled') return 'cancelled'
  return 'done'
}

/**
 * Label to the left of the dots row: "3 steps", or "3 steps, 1 failed".
 *
 * While a call is RUNNING the label says what it is — "Running 2 of 3: Run
 * Command — build_deck.py" — so a reader can see what the agent is doing
 * without opening the reasoning row or hovering a dot (Matt, 11 Sep, #1347
 * item 12). It is the dot tooltip's own derivation, so the words match what
 * the hover and the expanded detail row say. Once nothing is running, the
 * count is back.
 */
export function toolDotsLabel(toolCalls: ToolCallInfo[]): string {
  const runningIndex = toolCalls.findIndex(tc => tc.status === 'running')
  if (runningIndex >= 0) {
    const tc = toolCalls[runningIndex]
    const elapsed = tc.elapsedSeconds != null && tc.elapsedSeconds > 0 ? ` · ${tc.elapsedSeconds}s` : ''
    return `Running ${runningIndex + 1} of ${toolCalls.length}: ${toolDotTooltip(tc)}${elapsed}`
  }
  const failed = toolCalls.filter(tc => tc.status === 'error').length
  const cancelled = toolCalls.filter(tc => tc.status === 'cancelled').length
  const steps = `${toolCalls.length} ${toolCalls.length === 1 ? 'step' : 'steps'}`
  const notes = [failed > 0 ? `${failed} failed` : '', cancelled > 0 ? `${cancelled} cancelled` : ''].filter(Boolean)
  return notes.length ? `${steps}, ${notes.join(', ')}` : steps
}

/** Tooltip for one dot: the tool's display name plus the same one-line summary
 *  the expanded detail row shows (one derivation, not two). */
export function toolDotTooltip(toolCall: ToolCallInfo): string {
  const name = getToolDisplayName(toolCall.name, toolCall.input)
  const summary = getToolSummary(toolCall.name, toolCall.input)
  return summary ? `${name} — ${summary}` : name
}

/**
 * One dot in the collapsed tool-chain row: a hollow ring on a connector line,
 * the trace language FleetView uses (prompt/tool/reply rings on a rail).
 * The ring colour is the tool's kind; running pulses and trails a dashed
 * "working" tail, done fades, failed is a red ring.
 */
function ToolDot({ toolCall, last }: { toolCall: ToolCallInfo; last: boolean }) {
  const state = toolDotState(toolCall.status)
  const category = getToolCategory(toolCall.name)
  // A cancelled call (Stop, #1295) is a neutral ring whatever its kind.
  const colour = state === 'failed' ? 'error.main' : state === 'cancelled' ? 'text.disabled' : getToolColour(category)
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
      <Tooltip title={toolDotTooltip(toolCall)} placement="top">
        <Box
          data-testid="tool-dot"
          data-state={state}
          data-category={category}
          sx={{
            width: 10,
            height: 10,
            borderRadius: '50%',
            boxSizing: 'border-box',
            border: '2px solid',
            borderColor: colour,
            backgroundColor: 'background.paper',
            opacity: state === 'done' ? 0.55 : 1,
            ...(state === 'running' && {
              animation: 'toolDotPulse 1.2s ease-in-out infinite',
              '@keyframes toolDotPulse': {
                '0%, 100%': { boxShadow: '0 0 0 0 rgba(59,130,246,0.45)' },
                '50%': { boxShadow: '0 0 0 4px rgba(59,130,246,0)' },
              },
            }),
          }}
        />
      </Tooltip>
      {/* Connector to the next dot; after a running dot it is the dashed "working" tail. */}
      {(!last || state === 'running') && (
        <Box
          data-testid="tool-dot-rail"
          sx={{
            width: 14,
            height: 0,
            borderTop: '2px',
            borderTopStyle: state === 'running' ? 'dashed' : 'solid',
            borderTopColor: state === 'running' ? colour : 'divider',
          }}
        />
      )}
    </Box>
  )
}

/** Renders just the input/output detail for a single tool (no wrapping card). */
function ToolDetail({ toolCall }: { toolCall: ToolCallInfo }) {
  const [showFullOutput, setShowFullOutput] = useState(false)
  const [showHooks, setShowHooks] = useState(false)

  // A plain-text result that echoes an HTML file (a `cat`, a `pt files read`)
  // carries every inlined image as base64. Collapse those before the preview
  // window is cut, or the first 2000 characters are all image bytes. The
  // untouched output still feeds tryParseImageOutput below.
  const readableOutput = toolCall.output ? collapseBase64(toolCall.output) : undefined
  const isLargeOutput = (readableOutput?.length || 0) > OUTPUT_PREVIEW_LIMIT
  const displayOutput = readableOutput
    ? (isLargeOutput && !showFullOutput
        ? readableOutput.slice(0, OUTPUT_PREVIEW_LIMIT) + '...'
        : readableOutput)
    : undefined

  const imageData = toolCall.output ? tryParseImageOutput(toolCall.output) : null

  // Flags that are noise in the default view: they are almost always false and
  // say nothing about what the tool actually did. They move behind "More
  // information" rather than being dropped, so they stay available when
  // debugging.
  const DETAIL_OUTPUT_KEYS = new Set(['interrupted', 'isImage', 'noOutputExpected'])

  const isEmptyValue = (v: unknown): boolean =>
    v === null
    || v === undefined
    || v === ''
    || (Array.isArray(v) && v.length === 0)
    || (typeof v === 'object' && !Array.isArray(v) && Object.keys(v as object).length === 0)

  const parsedOutput = (() => {
    if (!toolCall.output) return null
    try { return JSON.parse(toolCall.output) } catch { return null }
  })()

  // Structured tool output is shown in two tiers: the fields that describe what
  // happened (command, stdout, ...) inline, and the low-signal flags behind the
  // "More information" disclosure alongside the hook events.
  const outputEntries = parsedOutput && typeof parsedOutput === 'object' && !Array.isArray(parsedOutput)
    ? Object.entries(parsedOutput as Record<string, unknown>)
    : []
  const primaryOutput = Object.fromEntries(outputEntries.filter(([k]) => !DETAIL_OUTPUT_KEYS.has(k)))
  const detailOutput = Object.fromEntries(outputEntries.filter(([k]) => DETAIL_OUTPUT_KEYS.has(k)))
  const detailCount = Object.keys(detailOutput).length + (toolCall.hookEvents?.length ?? 0)

  const renderValue = (value: unknown): React.ReactNode => {
    if (value === null || value === undefined) return <span style={{ color: '#9ca3af' }}>null</span>
    // Deliberately NOT colour-coded by value. These booleans are flags like
    // `interrupted` and `noOutputExpected`, where false is the GOOD outcome --
    // painting every false red made successful calls look like failures.
    if (typeof value === 'boolean') return <span style={{ color: '#6b7280' }}>{String(value)}</span>
    if (typeof value === 'number') return <span style={{ fontWeight: 500 }}>{value.toLocaleString()}</span>
    if (typeof value === 'string') {
      // Collapse first: a 200-character window onto an inlined image shows
      // 200 characters of base64 and nothing about the call.
      const shown = collapseBase64(value)
      if (shown.length > 200) return <span style={{ color: '#6b7280' }}>{shown.slice(0, 200)}...</span>
      return <span>{shown}</span>
    }
    return <span style={{ color: '#6b7280' }}>{JSON.stringify(value)}</span>
  }

  const renderKeyValue = (obj: Record<string, unknown>) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      {Object.entries(obj).filter(([, value]) => !isEmptyValue(value)).map(([key, value]) => (
        <Box key={key} sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
          <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', fontWeight: 500, minWidth: 60, flexShrink: 0 }}>
            {key}
          </Typography>
          <Typography sx={{ fontSize: '0.75rem', color: 'text.primary', wordBreak: 'break-word' }}>
            {renderValue(value)}
          </Typography>
        </Box>
      ))}
    </Box>
  )

  return (
    <Box sx={{ px: 3, pb: 1.5, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
      {toolCall.input && Object.keys(toolCall.input).length > 0 && (
        <Box sx={{ mb: 1.5 }}>
          <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.5 }}>Input</Typography>
          {renderKeyValue(toolCall.input)}
        </Box>
      )}

      {toolCall.output && (
        <Box>
          {imageData && (
            <Box sx={{ mb: 1 }}>
              <img
                src={`data:${imageData.mimeType};base64,${imageData.base64}`}
                style={{ maxWidth: '100%', maxHeight: 400, objectFit: 'contain', border: '1px solid rgba(0,0,0,0.06)' }}
              />
            </Box>
          )}
          {parsedOutput && typeof parsedOutput === 'object' && !Array.isArray(parsedOutput) ? (
            Object.keys(primaryOutput).length > 0 && (
              <Box sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 1 }}>
                <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.5 }}>Output</Typography>
                {renderKeyValue(primaryOutput)}
              </Box>
            )
          ) : (
            <>
              <Box
                component="pre"
                sx={{
                  m: 0,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all',
                  fontSize: '0.75rem',
                  maxHeight: showFullOutput ? 'none' : 300,
                  overflow: 'auto',
                  color: toolCall.status === 'error' ? 'text.secondary' : 'text.primary',
                }}
              >
                {displayOutput}
              </Box>
              {isLargeOutput && (
                <Button
                  size="small"
                  onClick={(e) => { e.stopPropagation(); setShowFullOutput(prev => !prev) }}
                  sx={{ fontSize: '0.7rem', textTransform: 'none', mt: 0.5, p: 0, color: 'text.secondary' }}
                >
                  {showFullOutput ? 'Show less' : 'Show more'}
                </Button>
              )}
            </>
          )}
        </Box>
      )}

      {detailCount > 0 && (
        <Box sx={{ mt: 1 }}>
          <Typography
            onClick={(e) => { e.stopPropagation(); setShowHooks(prev => !prev) }}
            sx={{
              fontWeight: 600, fontSize: 11, color: '#9ca3af', mb: 0.5,
              cursor: 'pointer', userSelect: 'none',
              '&:hover': { color: '#6b7280' },
            }}
          >
            MORE INFORMATION ({detailCount}) {showHooks ? '▾' : '▸'}
          </Typography>
          <Collapse in={showHooks} unmountOnExit>
            <Box sx={{ pl: 1, borderLeft: '2px solid #e5e7eb' }}>
              {Object.keys(detailOutput).length > 0 && (
                <Box sx={{ mb: 0.5 }}>{renderKeyValue(detailOutput)}</Box>
              )}
              {(toolCall.hookEvents ?? []).map((hook, i) => {
                const time = new Date(hook.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
                const stdout = hook.payload?.stdout as string | undefined
                const stderr = hook.payload?.stderr as string | undefined
                return (
                  <Box key={i} sx={{ mb: 0.5 }}>
                    <Typography sx={{ fontSize: 11, color: '#9ca3af', fontFamily: 'monospace' }}>
                      [{hook.hookType}] {time}
                    </Typography>
                    {(stdout || stderr) && (
                      <Box
                        component="pre"
                        sx={{
                          m: 0, mt: 0.25, fontSize: 11, color: '#6b7280',
                          fontFamily: 'monospace', whiteSpace: 'pre-wrap',
                          maxHeight: 100, overflow: 'auto',
                        }}
                      >
                        {stdout || stderr}
                      </Box>
                    )}
                  </Box>
                )
              })}
            </Box>
          </Collapse>
        </Box>
      )}
    </Box>
  )
}

// Spinner using CSS animation (avoids loaderGif dependency)
function Spinner({ size = 14 }: { size?: number }) {
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-block',
        width: size,
        height: size,
        border: '2px solid #e5e7eb',
        borderTopColor: '#3b82f6',
        borderRadius: '50%',
        animation: 'toolSpin 0.7s linear infinite',
        '@keyframes toolSpin': { '100%': { transform: 'rotate(360deg)' } },
        verticalAlign: 'middle',
      }}
    />
  )
}

/**
 * Glyph, colour and optional word for one tool call's status.
 *
 * 'cancelled' (#1295) is a stopped tool, not a failed one: it draws with the
 * same glyph as a failure but in the neutral grey, and says so in words.
 */
export function toolStatusPresentation(status: ToolCallInfo['status']): {
  icon: React.ReactNode
  color: string
  label?: string
} {
  switch (toolDotState(status)) {
    case 'running':
      return { icon: <Spinner />, color: '#3b82f6' }
    case 'failed':
      return { icon: '✗', color: '#9ca3af' }
    case 'cancelled':
      return { icon: '✗', color: '#9ca3af', label: 'cancelled' }
    default:
      return { icon: '✓', color: '#22c55e' }
  }
}

function ToolChain({ toolCalls, nested = false }: ToolCallGroupProps) {
  // Collapsed by default for every message, restored sessions included. The
  // chain never auto-expands: a running or failed call changes a dot's colour,
  // not the row's open state (#1273 D4).
  const [expanded, setExpanded] = useState(false)
  const [expandedToolId, setExpandedToolId] = useState<string | null>(null)
  const [singleDetailOpen, setSingleDetailOpen] = useState(false)

  const handleHeaderClick = () => {
    setExpanded(prev => !prev)
  }

  const handleToolRowClick = (toolId: string) => {
    setExpandedToolId(prev => prev === toolId ? null : toolId)
  }

  // Single tool call: render flat row instead of collapsible group
  if (toolCalls.length === 1) {
    const tc = toolCalls[0]
    const bareName = stripMcpPrefix(tc.name)
    const isCodeCreate = bareName === 'text_editor'
      && tc.input.command === 'create'
      && typeof tc.input.file_text === 'string'
      && typeof tc.input.file_path === 'string'

    if (isCodeCreate) {
      return (
        <Box sx={{ mt: 1 }}>
          <CodeCreatedBlock
            filePath={tc.input.file_path as string}
            code={tc.input.file_text as string}
            nested={nested}
          />
        </Box>
      )
    }

    const scriptMatch = parseScriptExecution(tc.name, tc.input)
    if (scriptMatch) {
      return (
        <Box sx={{ mt: 1 }}>
          <ScriptExecutionBlock
            nested={nested}
            scriptFile={scriptMatch.scriptFile}
            language={scriptMatch.language}
            fullCommand={scriptMatch.fullCommand}
            output={tc.output}
            status={tc.status}
            elapsedSeconds={tc.elapsedSeconds}
          />
        </Box>
      )
    }

    const displayName = getToolDisplayName(tc.name, tc.input)
    const category = getToolCategory(tc.name, tc.input)
    const categoryIcon = getToolIcon(category)
    const summary = getToolSummary(tc.name, tc.input)

    const { icon: statusIcon, color: statusColor, label: statusLabel } = toolStatusPresentation(tc.status)

    return (
      <Box
        data-testid="tool-card"
        sx={{
          ...CARD_SX,
          ...(nested && { borderRadius: INNER_RADIUS }),
          mt: 1,
          fontSize: '0.8125rem',
        }}
      >
        <Box
          onClick={() => setSingleDetailOpen(prev => !prev)}
          sx={{
            p: '8px 12px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            userSelect: 'none',
            '&:hover': { backgroundColor: 'action.hover' },
          }}
        >
          <Typography component="span" sx={{ fontSize: 13, width: 20, textAlign: 'center', flexShrink: 0 }}>
            {categoryIcon}
          </Typography>
          <Typography component="span" sx={{ fontWeight: 500, fontSize: 13, whiteSpace: 'nowrap', flexShrink: 0 }}>
            {displayName}
          </Typography>
          <Typography component="span" sx={{ color: statusColor, fontWeight: 600, fontSize: 13, flexShrink: 0 }}>
            {statusIcon}
          </Typography>
          {statusLabel && (
            <Typography component="span" sx={{ color: statusColor, fontSize: 12, flexShrink: 0 }}>
              {statusLabel}
            </Typography>
          )}
          {tc.elapsedSeconds !== undefined && (
            <Typography component="span" sx={{ color: '#9ca3af', fontSize: 12, flexShrink: 0 }}>
              {tc.elapsedSeconds.toFixed(1)}s
            </Typography>
          )}
          {summary && (
            <Typography
              component="span"
              sx={{
                color: '#6b7280',
                fontSize: 12,
                ml: 'auto',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                minWidth: 0,
                flexShrink: 1,
              }}
            >
              {summary}
            </Typography>
          )}
        </Box>
        <Collapse in={singleDetailOpen} unmountOnExit>
          <ToolDetail toolCall={tc} />
        </Collapse>
      </Box>
    )
  }

  // The collapsed chain: one dot per call, in call order.
  const dotsLabel = toolDotsLabel(toolCalls)
  const running = toolCalls.some(tc => tc.status === 'running')

  return (
    <Box
      data-testid="tool-chain-card"
      sx={{
        ...CARD_SX,
        ...(nested && { borderRadius: INNER_RADIUS }),
        mt: 1,
        fontSize: 13,
      }}
    >
      <Box
        component="button"
        type="button"
        onClick={handleHeaderClick}
        aria-expanded={expanded}
        data-testid="tool-dots-row"
        sx={{
          width: '100%',
          p: '4px 12px',
          minHeight: 26,
          backgroundColor: 'transparent',
          border: 'none',
          font: 'inherit',
          color: 'inherit',
          textAlign: 'left',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          userSelect: 'none',
          '&:hover': { backgroundColor: 'action.hover' },
        }}
      >
        {/* The label may carry a running step's name and summary, so it
            truncates rather than wraps, and takes the room the dots leave:
            the dots keep their width, the label gives way. Present, not
            loud, while running. */}
        <Typography
          variant="caption"
          component="span"
          data-testid="tool-dots-label"
          data-running={running ? 'true' : undefined}
          sx={{
            color: running ? 'text.primary' : 'text.secondary',
            fontWeight: running ? 500 : 400,
            flexShrink: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {dotsLabel}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, flexShrink: 0, flexWrap: 'wrap', rowGap: '4px' }}>
          {toolCalls.map((tc, i) => (
            <ToolDot key={tc.id} toolCall={tc} last={i === toolCalls.length - 1} />
          ))}
        </Box>
        <Typography component="span" sx={{ color: 'text.secondary', fontSize: 13, ml: 'auto', flexShrink: 0 }}>
          {expanded ? '▾' : '▸'}
        </Typography>
      </Box>

      <Collapse in={expanded} unmountOnExit>
        <Box sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
          {toolCalls.map(tc => {
            const bareName = stripMcpPrefix(tc.name)
            const isCodeCreate = bareName === 'text_editor'
              && tc.input.command === 'create'
              && typeof tc.input.file_text === 'string'
              && typeof tc.input.file_path === 'string'

            if (isCodeCreate) {
              return (
                <Box key={tc.id} sx={{ px: 1.5, py: 0.5 }}>
                  <CodeCreatedBlock
                    filePath={tc.input.file_path as string}
                    code={tc.input.file_text as string}
                    nested
                  />
                </Box>
              )
            }

            const scriptMatch = parseScriptExecution(tc.name, tc.input)
            if (scriptMatch) {
              return (
                <Box key={tc.id} sx={{ px: 1.5, py: 0.5 }}>
                  <ScriptExecutionBlock
                    nested
                    scriptFile={scriptMatch.scriptFile}
                    language={scriptMatch.language}
                    fullCommand={scriptMatch.fullCommand}
                    output={tc.output}
                    status={tc.status}
                    elapsedSeconds={tc.elapsedSeconds}
                  />
                </Box>
              )
            }

            const displayName = getToolDisplayName(tc.name, tc.input)
            const category = getToolCategory(tc.name, tc.input)
            const categoryIcon = getToolIcon(category)
            const summary = getToolSummary(tc.name, tc.input)
            const isExpanded = expandedToolId === tc.id

            const { icon: statusIcon, color: statusColor, label: statusLabel } = toolStatusPresentation(tc.status)

            return (
              <Box key={tc.id}>
                <Box
                  onClick={() => handleToolRowClick(tc.id)}
                  sx={{
                    p: '6px 12px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    '&:hover': { backgroundColor: 'action.hover' },
                    userSelect: 'none',
                  }}
                >
                  <Typography component="span" sx={{ fontSize: 13, width: 20, textAlign: 'center', flexShrink: 0 }}>
                    {categoryIcon}
                  </Typography>
                  <Typography component="span" sx={{ fontWeight: 500, fontSize: 13, whiteSpace: 'nowrap', flexShrink: 0 }}>
                    {displayName}
                  </Typography>
                  <Typography component="span" sx={{ color: statusColor, fontWeight: 600, fontSize: 13, flexShrink: 0 }}>
                    {statusIcon}
                  </Typography>
                  {statusLabel && (
                    <Typography component="span" sx={{ color: statusColor, fontSize: 12, flexShrink: 0 }}>
                      {statusLabel}
                    </Typography>
                  )}
                  {tc.elapsedSeconds !== undefined && (
                    <Typography component="span" sx={{ color: '#9ca3af', fontSize: 12, flexShrink: 0 }}>
                      {tc.elapsedSeconds.toFixed(1)}s
                    </Typography>
                  )}
                  {summary && (
                    <Typography
                      component="span"
                      sx={{
                        color: '#6b7280',
                        fontSize: 12,
                        ml: 'auto',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        minWidth: 0,
                        flexShrink: 1,
                      }}
                    >
                      {summary}
                    </Typography>
                  )}
                </Box>

                <Collapse in={isExpanded} unmountOnExit>
                  <ToolDetail toolCall={tc} />
                </Collapse>
              </Box>
            )
          })}
        </Box>
      </Collapse>
    </Box>
  )
}

/** An `Agent` or `Workflow` call: drawn as its own card, not a chain dot. */
function delegatedKind(tc: ToolCallInfo): 'subagent' | 'workflow' | null {
  const bareName = stripMcpPrefix(tc.name)
  if (tc.subagent || bareName === 'Agent' || bareName === 'Task') return 'subagent'
  if (tc.workflow || bareName === 'Workflow') return 'workflow'
  return null
}

/**
 * A message's tool calls. Sub-agents and workflows (Chief of Staff mode) are
 * each their own card, in call order; the tools between them form chains as
 * before. A message with neither is one chain, exactly as it always was.
 */
function ToolCallGroup({ toolCalls, nested = false }: ToolCallGroupProps) {
  if (!toolCalls.some(tc => delegatedKind(tc))) {
    return <ToolChain toolCalls={toolCalls} nested={nested} />
  }
  const parts: React.ReactNode[] = []
  let run: ToolCallInfo[] = []
  const flush = () => {
    if (run.length) parts.push(<ToolChain key={`chain-${run[0].id}`} toolCalls={run} nested={nested} />)
    run = []
  }
  for (const tc of toolCalls) {
    const kind = delegatedKind(tc)
    if (!kind) { run.push(tc); continue }
    flush()
    parts.push(kind === 'subagent'
      ? <SubagentCard key={tc.id} toolCall={tc} />
      : <WorkflowCard key={tc.id} toolCall={tc} />)
  }
  flush()
  return <>{parts}</>
}

// Memoised for the same reason as AgentMarkdown: hundreds of tool chains must
// not re-render because the chat's container changed shape.
export default memo(ToolCallGroup)
